// @ts-nocheck
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../../../../contexts/ProjectContext';
import {
  FiLoader as Loader2, FiRefreshCw as RefreshCw, FiSend as Rocket,
  FiExternalLink as ExternalLink
} from 'react-icons/fi';
import { getReleaseHistory } from '../../../../../services/aiOperationsApi';
import { ProjectBadge, formatUserName } from '../GovernancePage';

const ReleasesContent = () => {
  const navigate = useNavigate();
  const { projects } = useProject();
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [environmentFilter, setEnvironmentFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [promptFilter, setPromptFilter] = useState('');
  const [timePreset, setTimePreset] = useState('last_30_days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const uniqueUsers = useMemo(() => {
    const users = new Set(releases.map(r => r.released_by).filter(Boolean));
    return Array.from(users).sort((a, b) => a.localeCompare(b));
  }, [releases]);

  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    switch (timePreset) {
      case 'today':
        return { start: today.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
      case 'last_7_days': {
        const week = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
        return { start: week.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
      }
      case 'last_30_days': {
        const month = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
        return { start: month.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
      }
      case 'last_90_days': {
        const quarter = new Date(today.getTime() - 89 * 24 * 60 * 60 * 1000);
        return { start: quarter.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
      }
      case 'this_year': {
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return { start: yearStart.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
      }
      case 'custom':
        return { start: customStartDate, end: customEndDate };
      case 'all_time':
      default:
        return { start: '', end: '' };
    }
  };

  const loadReleases = useCallback(async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      const data = await getReleaseHistory({
        environment: environmentFilter || undefined,
        released_by: userFilter || undefined,
        prompt_name: promptFilter || undefined,
        start_date: dateRange.start || undefined,
        end_date: dateRange.end || undefined,
        page,
        limit: pageSize
      });
      setReleases(data || []);
    } catch (err) {
      console.error('Error loading releases:', err);
      setReleases([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentFilter, userFilter, promptFilter, timePreset, customStartDate, customEndDate, page, pageSize]);

  useEffect(() => {
    loadReleases();
  }, [loadReleases]);

  const handleViewPrompt = (promptId) => {
    navigate(`/ai-operations/prompts/${promptId}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <select
            value={environmentFilter}
            onChange={(e) => { setEnvironmentFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="">All Environments</option>
            <option value="development">Development</option>
            <option value="staging">Staging</option>
            <option value="production">Production</option>
          </select>
          <input
            type="text"
            placeholder="Filter by user..."
            value={userFilter}
            onChange={(e) => { setUserFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 w-40"
          />
          <input
            type="text"
            placeholder="Filter by prompt..."
            value={promptFilter}
            onChange={(e) => { setPromptFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 w-40"
          />
          <select
            value={timePreset}
            onChange={(e) => { setTimePreset(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="today">Today</option>
            <option value="last_7_days">Last 7 days</option>
            <option value="last_30_days">Last 30 days</option>
            <option value="last_90_days">Last 90 days</option>
            <option value="this_year">This year</option>
            <option value="all_time">All time</option>
            <option value="custom">Custom range</option>
          </select>
          {timePreset === 'custom' && (
            <>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              <span className="text-gray-500 text-sm">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </>
          )}
        </div>
        <button
          onClick={loadReleases}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      )}
      {!loading && releases.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12">
          <div className="max-w-md mx-auto text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Rocket className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Releases Yet</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              When prompts are promoted between environments, they will appear here with their release details.
            </p>
            <div className="text-left bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wide">How it works</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">1</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Create a prompt</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Start in development environment</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">2</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Promote to staging</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Test and validate your prompt</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">3</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Release to production</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Track all releases here</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {!loading && releases.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Prompt</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Version</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Environment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quality</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Released By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {releases.map((release) => {
                let releaseEnvClass: string;
                if (release.environment === 'production') releaseEnvClass = 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300';
                else if (release.environment === 'staging') releaseEnvClass = 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
                else releaseEnvClass = 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';

                let releaseTypeClass: string;
                if (release.release_type === 'promotion') releaseTypeClass = 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300';
                else if (release.release_type === 'rollback') releaseTypeClass = 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300';
                else releaseTypeClass = 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';

                let releaseTypeLabel: string;
                if (release.release_type === 'promotion') releaseTypeLabel = release.approval_request_id ? 'Approved' : 'Direct';
                else if (release.release_type === 'rollback') releaseTypeLabel = 'Rollback';
                else if (release.release_type === 'initial') releaseTypeLabel = 'Initial';
                else releaseTypeLabel = release.release_type;

                let passRateClass: string;
                if (release.pass_rate >= 0.8) passRateClass = 'text-green-600';
                else if (release.pass_rate >= 0.5) passRateClass = 'text-yellow-600';
                else passRateClass = 'text-red-600';

                return (
                <tr key={release.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{release.prompt_name}</div>
                      <ProjectBadge name={release.project_id ? (projects.find((project) => project.id === release.project_id)?.name ?? 'Project') : null} />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500 dark:text-gray-400">v{release.version_number}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${releaseEnvClass}`}>
                      {release.environment}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${releaseTypeClass}`}>
                      {releaseTypeLabel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {release.pass_rate == null ? (
                      <span className="text-xs text-gray-400">N/A</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${passRateClass}`}>
                          {(release.pass_rate * 100).toFixed(0)}%
                        </span>
                        {release.avg_score != null && (
                          <span className="text-xs text-gray-400">
                            (avg: {(release.avg_score * 100).toFixed(0)}%)
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{formatUserName(release.released_by)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(release.released_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => handleViewPrompt(release.prompt_id)}
                      className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      title="View prompt"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          {releases.length > 0 && (
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Showing {releases.length} releases
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500">Page {page}</span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={releases.length < pageSize}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReleasesContent;
